/* eslint-disable no-nested-ternary */
import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import styled from '@emotion/styled';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import IcomoonReact from 'icomoon-react';
import { useTheme } from 'emotion-theming';
import Shimmer from '../../../../shared/_TBComponents/Shimmer';
import Text from '../../../../shared/components/Text/Text';
import CalendarDropdown from './CalendarDropdown';
import WorkoutColorCircle from './WorkoutColorCircle';
import useWorkoutColorUpdate from '../../hooks/useWorkoutColorUpdate';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import {
  setActiveModal,
  setExpandedItems,
  updateNonDateBasedWorkoutInfo,
  setPreviewDayNumber,
  setIsPrimaryAnimatedSideBarOpen,
  setPrimarySideBarDataTag,
} from '../../ducks/calendarActions';

const Wrapper = styled('div')`
  display: flex;
  width: 100%;
  /* Left padding keeps the 3px that the old color border used to occupy, so
     content stays aligned now that the color line is replaced by the circle. */
  padding: 4px 8px 4px 11px;
  background: ${({ theme }) => theme.colors.surface};
  justify-content: space-between;
  min-height: 58px;
`;

// Row holding the "WEEK X - DAY Y" text and the workout-color circle inline.
const WeekDayRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 8px;
`;

const TitlesContainer = styled('div')`
  display: flex;
  gap: 8px;
  flex-direction: column;
  width: calc(100% - 56px);
`;

const ExpandAllAndDropdownContainer = styled('div')`
  display: flex;
  align-items: center;
`;

const CopyContainer = styled('div')`
  height: 28px;
  width: 28px;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  
  :hover {
    cursor: pointer;
    background: rgba(119, 167, 255, 0.2);
  }

  &:hover .copyIcon {
    fill: ${({ theme }) => theme.colors.hover};
  }
`;

const DropdownContainer = styled('div')`
  flex-shrink: 0;
  min-width: 28px;
  min-height: 28px;
  padding-right: 5px;
  justify-content: center;
  display: flex;
`;

const PreviewContainer = styled('div')`
  height: 28px;
  width: 28px;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;

  :hover {
    cursor: pointer;
    background: rgba(119, 167, 255, 0.2);
  }
`;

const ExpandCollapseContainer = styled('div')`
  :hover {
    cursor: pointer;
    background: rgba(119, 167, 255, 0.2);
  }
  height: 28px;
  width: 28px;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;

  .expandCollapseIcon {
    fill: ${({ theme }) => theme.colors.textTertiary};
  }

  &:hover .expandCollapseIcon {
    fill: ${({ theme }) => theme.colors.hover};
  }
`;

const Name = styled('div')`
  font-family: 'Nunito Sans';
  font-weight: 400;
  font-size: 16px;
  color: ${({ theme }) => theme.colors.text};
  display: flex;
  align-items: center;
  width: 100%;
  overflow: hidden;
  cursor: pointer;
`;

const Input = styled('input')`
  font-family: 'Nunito Sans';
  font-weight: 400;
  font-size: 16px;
  color: ${({ theme }) => theme.colors.text};
  border: none;
  outline: none;
  width: 100%;
  &::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
  max-height: 24px;
`;

const ShimmerTitle = styled('div')`
  width: 200px;
  height: 24px;
  max-height: 24px;
  border-radius: 4px;
`;

const WeekAndDayShimmerTitle = styled('div')`
  width: 125px;
  height: 18px;
  max-height: 24px;
  border-radius: 4px;
`;

const ProgramLibrarySubHeader = () => {
  const { colors } = useTheme();
  const dispatch = useDispatch();
  const textRef = useRef(null);
  const [hovering, sethovering] = useState(false);
  const [copyHovering, setCopyHovering] = useState(false);
  const [previewHovering, setPreviewHovering] = useState(false);
  const [workoutName, setWorkoutName] = useState('');
  const [, setWorkoutValue] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const selectedProgramDayNumber = useSelector(
    (state) => state.calendar.data.selectedProgramDayNumber,
  );
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const date = new Date(selectedDate);
  const endpointFormattedDate = moment(date).format('YYYY-MM-DD');
  const programType = useSelector((state) => state.calendar.data.programType);
  const viewType = useSelector((state) => state.calendar.data.viewType);
  const selectedReduxNonDateBasedProgram = useSelector(
    (state) => state.calendar.data.selectedReduxNonDateBasedProgram,
  );
  const isPrimaryAnimatedSideBarOpen = useSelector(
    (state) => state.calendar.data.isPrimaryAnimatedSideBarOpen,
  );
  const isSavedItemsLoading = useSelector((state) => state.calendar.ui.isSavedItemsLoading);
  const calendarItems = programType === 'free-form-program'
    ? useSelector((state) => state.calendar.data.nonDateBasedProgramItems[
      selectedProgramDayNumber
    ]) : useSelector(
      (state) => state.calendar.data.calendarItems[endpointFormattedDate],
    );
  const nonDateBasedProgramItems = useSelector(
    (state) => state.calendar.data.nonDateBasedProgramItems,
  );
  const isAllExercisesExpanded = useSelector((state) => {
    const ei = state.calendar.data.expandedItems;
    const values = Object.values(ei || {});
    return values.length > 0 && values.every((v) => v === true);
  });

  const { colorOptions, handleUpdateColor, displayColor } = useWorkoutColorUpdate({
    programType,
    workoutDetails: calendarItems,
    effectiveDate: selectedDate,
    effectiveDayNumber: selectedProgramDayNumber,
  });

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const checkOverflow = () => {
      if (textRef.current) {
        setIsOverflowing(textRef.current.scrollWidth > textRef.current.clientWidth);
      }
    };

    const timeoutId = setTimeout(checkOverflow, 310);
    window.addEventListener('resize', checkOverflow);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkOverflow);
    };
  }, [workoutName, windowWidth, isPrimaryAnimatedSideBarOpen]);

  useEffect(() => {
    if (!calendarItems?.title || calendarItems.title.trim() === '') {
      setWorkoutName('Add Workout Name');
    } else {
      setWorkoutName(calendarItems.title);
    }
  }, [calendarItems]);

  const handleExpandExercises = () => {
    const newExpandedItems = {};
    /** Check if workoutDetails and exercises exist */
    if (calendarItems && calendarItems.exercises && calendarItems.exercises.length > 0) {
      /** Iterate over all exercises for the selected date */
      calendarItems.exercises.forEach((exercise) => {
        /** Set each assignedId as a key with value true */
        if (exercise.assignedId) {
          newExpandedItems[exercise.assignedId] = true;
        }
      });
    }
    dispatch(setExpandedItems(newExpandedItems));
  };

  const handleCollapseExercises = () => {
    const newExpandedItems = {};
    /** Check if workoutDetails and exercises exist */
    if (calendarItems && calendarItems.exercises && calendarItems.exercises.length > 0) {
      /** Iterate over all exercises for the selected date */
      calendarItems.exercises.forEach((exercise) => {
        /** Set each assignedId as a key with value false */
        if (exercise.assignedId) {
          newExpandedItems[exercise.assignedId] = false;
        }
      });
    }
    dispatch(setExpandedItems(newExpandedItems));
  };

  const handleEditClick = () => {
    if (!isSavedItemsLoading) {
      setIsEditing(true);
    }
  };

  const handleInputChange = (e) => {
    setWorkoutName(e.target.value);
    setWorkoutValue(e.target.value);
  };

  const submitWorkoutName = () => {
    const titleToSubmit = workoutName === 'Add Workout Name' ? '' : workoutName;

    const submissionObject = {
      color: calendarItems?.color || '',
      title: titleToSubmit,
    };

    const savedWorkoutId = selectedReduxNonDateBasedProgram.id;
    dispatch(updateNonDateBasedWorkoutInfo(
      currentUser.accountCode,
      savedWorkoutId,
      selectedProgramDayNumber,
      submissionObject,
      'name',
    ));
    setWorkoutValue(null);
  };

  const handleInputBlur = () => {
    setIsEditing(false);

    const currentTitle = calendarItems?.title || '';
    const newTitle = workoutName === 'Add Workout Name' ? '' : workoutName;

    if (currentTitle === newTitle) {
      setWorkoutValue(null);
      return;
    }

    submitWorkoutName();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      setIsEditing(false);

      const currentTitle = calendarItems?.title || '';
      const newTitle = workoutName === 'Add Workout Name' ? '' : workoutName;

      if (currentTitle === newTitle) {
        setWorkoutValue(null);
        return;
      }

      submitWorkoutName();
    }
  };

  const renderWorkoutName = () => (
    <Name onClick={handleEditClick} data-tooltip-id='workoutName'>
      {isSavedItemsLoading ? (
        <Shimmer>
          <ShimmerTitle />
        </Shimmer>
      ) : isEditing ? (
        <Input
          value={workoutName === 'Add Workout Name' ? '' : workoutName}
          placeholder={workoutName === 'Add Workout Name' ? workoutName : ''}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyPress}
          autoFocus
        />
      ) : (
        workoutName === 'Add Workout Name' ? (
          <Text style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} fontSize='16px' color={colors.textSecondary}>{workoutName}</Text>
        ) : (
          <Text ref={textRef} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} fontWeight={700} fontSize='16px' color={colors.text}>{workoutName}</Text>
        )
      )}
      {isOverflowing && (
        <ReactTooltip
          id='workoutName'
          place='top'
          content={(
            <Text color={colors.white} fontSize='13px'>
              {workoutName}
            </Text>
              )}
          style={{
            backgroundColor: 'rgba(68, 68, 68, 1)',
            color: colors.white,
            fontFamily: 'Nunito Sans',
            fontSize: '13px',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            maxWidth: Math.min(windowWidth - 40, 800),
            padding: '8px',
            zIndex: 9999,
            textAlign: 'center',
          }}
        />
      )}
    </Name>
  );
  return (
    <Wrapper>
      <TitlesContainer>
        {isSavedItemsLoading ? (
          <Shimmer>
            <WeekAndDayShimmerTitle />
          </Shimmer>
        ) : (
          <WeekDayRow>
            <Text style={{ whiteSpace: 'nowrap' }} fontSize='12px' fontWeight='700' color={colors.text}>{`WEEK ${calendarItems?.weekNumber} - DAY ${calendarItems?.dayInWeek}`}</Text>
            <WorkoutColorCircle
              colorOptions={colorOptions}
              currentColor={displayColor}
              onSelect={handleUpdateColor}
              marginTop={-1}
            />
          </WeekDayRow>
        )}
        {!isSavedItemsLoading && !calendarItems?.exercises?.length ? (
          <Text fontSize='16px' fontWeight='700'>
            Rest Day
          </Text>
        ) : (
          <>
            {renderWorkoutName()}
          </>
        )}
      </TitlesContainer>
      <ExpandAllAndDropdownContainer>
        {/* Day view hides the header preview eye for free-form programs; the dropdown menu still
            offers the preview. Other views (e.g. month) keep the eye. */}
        {viewType !== 'dayView' ? (
          <>
            <PreviewContainer
              data-tooltip-id='header-preview-workout'
              onMouseEnter={() => setPreviewHovering(true)}
              onMouseLeave={() => setPreviewHovering(false)}
              onClick={() => {
                // Open the phone sidebar preview on this program day (mirrors IconSideBar).
                dispatch(setPreviewDayNumber(selectedProgramDayNumber));
                if (!isPrimaryAnimatedSideBarOpen) {
                  dispatch(setIsPrimaryAnimatedSideBarOpen(true));
                  setTimeout(() => dispatch(setPrimarySideBarDataTag('phone')), 250);
                } else {
                  dispatch(setPrimarySideBarDataTag('phone'));
                }
              }}
            >
              <IcomoonReact
                iconSet={iconSet}
                size={16}
                icon='visible'
                color={previewHovering ? colors.hover : colors.textSecondary}
              />
            </PreviewContainer>
            <ReactTooltip
              id='header-preview-workout'
              place='left'
              content='Preview Workout'
              style={{
                backgroundColor: 'rgba(68, 68, 68, 1)',
                color: 'white',
                fontFamily: 'Nunito Sans',
                fontSize: '13px',
                zIndex: 1000,
              }}
            />
          </>
        ) : null}
        {programType !== 'free-form-program' ? (
          <CopyContainer
            data-tooltip-id='header-copy-workout'
            onMouseEnter={() => setCopyHovering(true)}
            onMouseLeave={() => setCopyHovering(false)}
            onClick={() => {
              let modalData = null;

              // Auto-preselect all items for Program Library mode
              if (programType === 'free-form-program' && nonDateBasedProgramItems) {
                const dayData = nonDateBasedProgramItems[selectedProgramDayNumber];
                if (dayData && dayData.exercises && dayData.exercises.length > 0) {
                  const preSelectedItems = {};
                  dayData.exercises.forEach((exercise) => {
                    // Only include items with numeric assignedId (filter out strings)
                    if (exercise.assignedId && typeof exercise.assignedId === 'number') {
                      preSelectedItems[exercise.assignedId] = exercise;
                    }
                  });

                  modalData = {
                    preSelectedItems,
                    skipFirstSlide: true,
                    sourceDayNumber: selectedProgramDayNumber,
                    sourceWeekNumber: dayData.weekNumber,
                    sourceDayInWeek: dayData.dayInWeek,
                  };
                }
              }

              dispatch(setActiveModal('copyWorkoutModal', modalData));
            }}
          >
            <IcomoonReact
              iconSet={iconSet}
              size={17}
              icon='duplicate'
              color={copyHovering ? colors.hover : colors.textSecondary}
            />
          </CopyContainer>
        ) : null}
        <ReactTooltip
          id='header-copy-workout'
          place='left'
          content='Copy Workout'
          style={{
            backgroundColor: 'rgba(68, 68, 68, 1)',
            color: colors.white,
            fontFamily: 'Nunito Sans',
            fontSize: '13px',
            zIndex: 1000,
          }}
        />
        <ExpandCollapseContainer
          onMouseEnter={() => sethovering(true)}
          onMouseLeave={() => sethovering(false)}
          onClick={() => {
            if (isAllExercisesExpanded) {
              handleCollapseExercises();
            } else {
              handleExpandExercises();
            }
          }}
          data-tooltip-id='header-expand-all'
        >
          <IcomoonReact
            iconSet={iconSet}
            size={15}
            icon={isAllExercisesExpanded ? 'collapse' : 'expand2'}
            color={hovering ? colors.hover : colors.textSecondary}
          />
          <ReactTooltip
            id='header-expand-all'
            place='left'
            content={isAllExercisesExpanded ? 'Collapse All' : 'Expand All'}
            style={{
              backgroundColor: 'rgba(68, 68, 68, 1)',
              color: colors.white,
              fontFamily: 'Nunito Sans',
              fontSize: '13px',
              zIndex: 1000,
            }}
          />
        </ExpandCollapseContainer>
        <DropdownContainer>
          <CalendarDropdown />
        </DropdownContainer>
      </ExpandAllAndDropdownContainer>
    </Wrapper>
  );
};

export default ProgramLibrarySubHeader;
